<template>
  <div class="master-create-form">
    <!-- マスタタイプ選択 -->
    <div class="mb-3">
      <label class="form-label">マスタタイプ <span class="text-danger">*</span></label>
      <select 
        v-model="selectedMasterType" 
        class="form-select"
        :disabled="loading"
        @change="onMasterTypeChange"
      >
        <option value="">マスタタイプを選択してください</option>
        <option 
          v-for="masterType in masterTypeOptions" 
          :key="masterType.value" 
          :value="masterType.value"
        >
          {{ masterType.label }}
        </option>
      </select>
    </div>

    <!-- 動的フォームフィールド -->
    <div v-if="selectedMasterType && formFields.length > 0">
      <h5 class="mb-3">{{ getMasterDisplayName(selectedMasterType) }} 新規作成</h5>
      
      <form @submit.prevent="handleSubmit">
        <div v-for="field in formFields" :key="field.name" class="mb-3">
          <label :for="field.name" class="form-label">
            {{ field.label }}
            <span v-if="field.required" class="text-danger">*</span>
          </label>
          
          <!-- テキスト入力 -->
          <input
            v-if="field.type === 'text' || field.type === 'varchar'"
            :id="field.name"
            v-model="formData[field.name]"
            type="text"
            class="form-control"
            :placeholder="field.placeholder"
            :required="field.required"
            :disabled="loading"
          />
          
          <!-- 数値入力 -->
          <input
            v-else-if="field.type === 'number' || field.type === 'integer' || field.type === 'decimal'"
            :id="field.name"
            v-model.number="formData[field.name]"
            type="number"
            class="form-control"
            :placeholder="field.placeholder"
            :required="field.required"
            :disabled="loading"
          />
          
          <!-- 日付入力 -->
          <input
            v-else-if="field.type === 'date'"
            :id="field.name"
            v-model="formData[field.name]"
            type="date"
            class="form-control"
            :required="field.required"
            :disabled="loading"
          />
          
          <!-- テキストエリア -->
          <textarea
            v-else-if="field.type === 'textarea'"
            :id="field.name"
            v-model="formData[field.name]"
            class="form-control"
            :placeholder="field.placeholder"
            :required="field.required"
            :disabled="loading"
            rows="3"
          ></textarea>
          
          <!-- セレクト -->
          <select
            v-else-if="field.type === 'select'"
            :id="field.name"
            v-model="formData[field.name]"
            class="form-select"
            :required="field.required"
            :disabled="loading"
          >
            <option value="">選択してください</option>
            <option 
              v-for="option in field.options" 
              :key="option.value" 
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          
          <div v-if="field.help" class="form-text">{{ field.help }}</div>
        </div>

        <!-- 操作ボタン -->
        <div class="d-flex gap-2 mt-4">
          <button 
            type="submit" 
            class="btn btn-primary"
            :disabled="loading || !canSubmit"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ loading ? '作成中...' : '作成' }}
          </button>
          <button 
            type="button" 
            class="btn btn-secondary"
            @click="resetForm"
            :disabled="loading"
          >
            リセット
          </button>
          <button 
            type="button" 
            class="btn btn-outline-secondary"
            @click="$emit('cancel')"
            :disabled="loading"
          >
            キャンセル
          </button>
        </div>
      </form>
    </div>

    <!-- マスタタイプ未選択時のメッセージ -->
    <div v-else-if="!selectedMasterType" class="text-muted text-center py-4">
      <i class="fas fa-info-circle me-2"></i>
      マスタタイプを選択してください
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { MasterApiService } from '@/services/MasterApiService'
import { getMasterFormFields, getMasterDisplayName } from '@/utils/masterHelpers'

export default {
  name: 'MasterCreateForm',
  components: {
    // BsMsgAlertコンポーネントを削除
  },
  props: {
    masterTypeOptions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['created', 'cancel', 'alert'],
  setup(props, { emit }) {
    // State
    const selectedMasterType = ref('')
    const formData = ref({})
    const loading = ref(false)

    // Form fields
    const formFields = computed(() => {
      if (!selectedMasterType.value) return []
      return getMasterFormFields(selectedMasterType.value)
    })

    // Submit validation
    const canSubmit = computed(() => {
      if (!selectedMasterType.value || formFields.value.length === 0) return false
      
      // 必須フィールドのチェック
      const requiredFields = formFields.value.filter(field => field.required)
      return requiredFields.every(field => {
        const value = formData.value[field.name]
        return value !== undefined && value !== null && value !== ''
      })
    })

    // Methods
    const emitAlert = (message, alertClass = 'alert-info') => {
      emit('alert', { message, class: alertClass })
    }

    const onMasterTypeChange = () => {
      resetFormData()
      if (selectedMasterType.value) {
        emitAlert(`${getMasterDisplayName(selectedMasterType.value)}の新規作成フォームです。`, 'alert-info')
      }
    }

    const resetFormData = () => {
      formData.value = {}
      // 必須フィールドのデフォルト値設定
      formFields.value.forEach(field => {
        if (field.default !== undefined) {
          formData.value[field.name] = field.default
        }
      })
    }

    const resetForm = () => {
      resetFormData()
      emitAlert('フォームをリセットしました。', 'alert-info')
    }

    const handleSubmit = async () => {
      if (!canSubmit.value) {
        emitAlert('必須フィールドを入力してください。', 'alert-warning')
        return
      }

      loading.value = true
      emitAlert('作成中...', 'alert-info')

      try {
        const result = await MasterApiService.createMasterItem(selectedMasterType.value, formData.value)
        
        if (result.success) {
          emitAlert('マスタデータを作成しました。', 'alert-success')
          emit('created', result.data)
          // フォームリセット
          setTimeout(() => {
            resetForm()
            selectedMasterType.value = ''
          }, 1000)
        } else {
          emitAlert(result.message || 'データの作成に失敗しました。', 'alert-danger')
        }
      } catch (error) {
        console.error('Create error:', error)
        emitAlert('システムエラーが発生しました。', 'alert-danger')
      } finally {
        loading.value = false
      }
    }

    // Watch for master type change to reset form
    watch(selectedMasterType, () => {
      resetFormData()
    })

    return {
      selectedMasterType,
      formData,
      formFields,
      loading,
      canSubmit,
      onMasterTypeChange,
      resetForm,
      handleSubmit,
      getMasterDisplayName
    }
  }
}
</script>

<style scoped>
.master-create-form {
  max-width: 800px;
  margin: 0 auto;
}

.form-label {
  font-weight: 600;
}

.text-danger {
  color: #dc3545 !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style> 